import React, { useState, useEffect, useMemo } from 'react';
import StatusDot from './StatusDot';

const api = (path) => fetch(`/api${path}`).then(r => r.ok ? r.json() : null).catch(() => null);

const TABS = [
  { key: 'topology', label: 'Topology' },
  { key: 'map', label: 'Map' },
  { key: 'flows', label: 'Flows' },
  { key: 'impact', label: 'Impact' },
  { key: 'timeline', label: 'Timeline' },
];

function ServiceBox({ service, accent, selected, onClick }) {
  const isSelected = selected === service.id;
  return (
    <div onClick={() => onClick(service)} className="card" style={{
      padding: 'var(--gap-sm)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 'var(--gap-sm)',
      borderColor: isSelected ? accent : 'var(--border)', borderWidth: isSelected ? 2 : 1,
    }}>
      <StatusDot status={service.critical ? 'warn' : 'ok'} size={6} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>{service.name}</div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{service.type}{service.port ? ` :${service.port}` : ''}</div>
      </div>
      {service.critical && <span className="badge badge-warn" style={{ fontSize: 8 }}>kritisch</span>}
    </div>
  );
}

function FlowArrow({ from, to, label, accent }) {
  return (
    <div className="card" style={{ padding: 'var(--gap-xs) var(--gap-sm)', display: 'flex', alignItems: 'center', gap: 'var(--gap-sm)' }}>
      <span style={{ fontWeight: 600, fontSize: 'var(--font-size-xs)', color: accent, minWidth: 100 }}>{from}</span>
      <span style={{ color: 'var(--text-muted)' }}>→</span>
      <span style={{ fontWeight: 500, fontSize: 'var(--font-size-xs)', minWidth: 100 }}>{to}</span>
      {label && <span style={{ flex: 1, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right' }}>{label}</span>}
    </div>
  );
}

// ── Topology Network Diagram ──────────────────────

const TOPO_NODES = [
  { id: 'internet', label: 'Internet', icon: '🌐', x: 400, y: 30, type: 'cloud' },
  { id: 'cloudflare', label: 'Cloudflare DNS', icon: '☁', x: 400, y: 100, type: 'service', info: '*.devboxnsa.org' },
  { id: 'devbox', label: 'vmd181486', icon: '🖥', x: 400, y: 240, type: 'server', ip: '207.180.207.183', vpn: '100.64.0.1', info: 'Ubuntu 24.04 · 6C · 12GB', services: ['Nginx', 'Docker (71)', 'Ollama', 'Headscale'] },
  { id: 'laptop', label: 'pop-os', icon: '💻', x: 150, y: 360, type: 'device', ip: '100.64.0.2', info: 'Pop!_OS 22.04 · Laptop' },
  { id: 'homeserver', label: 'Home Server', icon: '🏠', x: 650, y: 360, type: 'device', ip: '100.64.0.6', info: 'Pop!_OS · GTX 1050 Ti · HDD 916GB' },
  { id: 'tablet', label: 'Samsung Tab', icon: '📱', x: 150, y: 480, type: 'device', ip: '100.69.15.19', info: 'Android · NetBird' },
  { id: 'phone', label: 'Samsung S22', icon: '📱', x: 400, y: 480, type: 'device', info: 'Android · NetBird' },
  { id: 'stick', label: 'SECURESTICK', icon: '🔒', x: 650, y: 480, type: 'device', info: 'USB verschluesselt' },
];

const TOPO_LINKS = [
  { from: 'internet', to: 'cloudflare', type: 'https', label: 'DNS' },
  { from: 'cloudflare', to: 'devbox', type: 'https', label: 'HTTPS :443' },
  { from: 'devbox', to: 'laptop', type: 'vpn', label: 'Headscale VPN' },
  { from: 'devbox', to: 'homeserver', type: 'vpn', label: 'Headscale VPN' },
  { from: 'devbox', to: 'tablet', type: 'vpn', label: 'NetBird', style: 'dashed' },
  { from: 'devbox', to: 'phone', type: 'vpn', label: 'NetBird', style: 'dashed' },
  { from: 'devbox', to: 'homeserver', type: 'ssh', label: 'rsync Backup 04:15' },
  { from: 'laptop', to: 'devbox', type: 'ssh', label: 'SSH :2222' },
];

const LINK_STYLES = {
  vpn: { stroke: '#9ece6a', dashArray: '8,4', animated: true },
  https: { stroke: '#7aa2f7', dashArray: 'none', animated: false },
  ssh: { stroke: '#e0af68', dashArray: '3,3', animated: false },
};

function TopologyView({ accent, domain }) {
  const [nodeStatus, setNodeStatus] = useState({});
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    fetch('/api/nodes?domain=ops').then(r => r.ok ? r.json() : null).then(d => {
      const status = {};
      (d?.nodes || []).forEach(n => {
        if (n.name?.toLowerCase().includes('devbox') || n.name?.includes('vmd')) status.devbox = 'online';
        if (n.name?.toLowerCase().includes('pop') || n.name?.toLowerCase().includes('laptop')) status.laptop = n.status;
        if (n.name?.toLowerCase().includes('home')) status.homeserver = n.status;
        if (n.name?.toLowerCase().includes('tab')) status.tablet = n.status;
      });
      status.devbox = 'online'; // always online if we can reach API
      status.internet = 'online';
      status.cloudflare = 'online';
      setNodeStatus(status);
    }).catch(() => {});
  }, []);

  const getStatus = (id) => nodeStatus[id] === 'online' ? '#9ece6a' : '#565f89';
  const getGlow = (id) => nodeStatus[id] === 'online' ? '0 0 8px rgba(158,206,106,0.4)' : 'none';
  const W = 800, H = 540;

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--gap-sm)' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', maxWidth: 800, height: 'auto' }}>
        <defs>
          {/* Animated dash for VPN tunnels */}
          <style>{`
            @keyframes dashFlow { to { stroke-dashoffset: -24; } }
            .vpn-animated { animation: dashFlow 1.5s linear infinite; }
            @keyframes pulse { 0%,100% { opacity: 0.6; } 50% { opacity: 1; } }
            .status-pulse { animation: pulse 2s ease-in-out infinite; }
          `}</style>
          <marker id="arrowGreen" viewBox="0 0 10 7" refX="10" refY="3.5" markerWidth="8" markerHeight="6" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#9ece6a" opacity="0.6" />
          </marker>
          <marker id="arrowBlue" viewBox="0 0 10 7" refX="10" refY="3.5" markerWidth="8" markerHeight="6" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#7aa2f7" opacity="0.6" />
          </marker>
          <marker id="arrowYellow" viewBox="0 0 10 7" refX="10" refY="3.5" markerWidth="8" markerHeight="6" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#e0af68" opacity="0.6" />
          </marker>
        </defs>

        {/* Background */}
        <rect width={W} height={H} rx="12" fill="#1a1b26" />

        {/* Links */}
        {TOPO_LINKS.map((link, i) => {
          const from = TOPO_NODES.find(n => n.id === link.from);
          const to = TOPO_NODES.find(n => n.id === link.to);
          if (!from || !to) return null;
          const ls = LINK_STYLES[link.type] || LINK_STYLES.https;
          const marker = link.type === 'vpn' ? 'url(#arrowGreen)' : link.type === 'ssh' ? 'url(#arrowYellow)' : 'url(#arrowBlue)';
          // Offset duplicate links
          const dupes = TOPO_LINKS.filter(l => (l.from === link.from && l.to === link.to) || (l.from === link.to && l.to === link.from));
          const dupeIdx = dupes.indexOf(link);
          const offset = dupes.length > 1 ? (dupeIdx - (dupes.length - 1) / 2) * 15 : 0;
          const mx = (from.x + to.x) / 2 + offset;
          const my = (from.y + to.y) / 2 + offset;
          return (
            <g key={i}>
              <path
                d={`M ${from.x} ${from.y} Q ${mx + offset} ${my - offset * 0.5} ${to.x} ${to.y}`}
                fill="none" stroke={ls.stroke} strokeWidth="1.5" opacity="0.5"
                strokeDasharray={ls.dashArray} markerEnd={marker}
                className={ls.animated ? 'vpn-animated' : ''}
              />
              {/* Link label */}
              <text x={mx} y={my - 6} textAnchor="middle" fontSize="8" fill="#565f89" fontFamily="Inter, sans-serif">
                {link.label}
              </text>
            </g>
          );
        })}

        {/* Nodes */}
        {TOPO_NODES.map(node => {
          const isHovered = hovered === node.id;
          const online = nodeStatus[node.id] === 'online';
          const w = node.type === 'server' ? 180 : node.type === 'cloud' ? 120 : 130;
          const h = node.type === 'server' ? 90 : 44;
          return (
            <g key={node.id} onMouseEnter={() => setHovered(node.id)} onMouseLeave={() => setHovered(null)}
              style={{ cursor: 'pointer' }}>
              {/* Glow */}
              {online && <rect x={node.x - w / 2 - 2} y={node.y - h / 2 - 2} width={w + 4} height={h + 4} rx="10"
                fill="none" stroke={getStatus(node.id)} strokeWidth="1" opacity="0.3" className="status-pulse" />}
              {/* Box */}
              <rect x={node.x - w / 2} y={node.y - h / 2} width={w} height={h} rx="8"
                fill={isHovered ? '#24283b' : '#1f2335'}
                stroke={online ? getStatus(node.id) : '#3b4261'} strokeWidth={isHovered ? 2 : 1} />
              {/* Status dot */}
              <circle cx={node.x + w / 2 - 10} cy={node.y - h / 2 + 10} r="4" fill={getStatus(node.id)} />
              {/* Icon + Label */}
              <text x={node.x - w / 2 + 10} y={node.y - (node.type === 'server' ? 12 : 2)} fontSize="12" fill="#c0caf5" fontFamily="Inter, sans-serif" fontWeight="600">
                {node.icon} {node.label}
              </text>
              {/* IP */}
              {node.ip && <text x={node.x - w / 2 + 10} y={node.y + (node.type === 'server' ? 4 : 14)} fontSize="9" fill="#565f89" fontFamily="JetBrains Mono, monospace">
                {node.vpn ? `${node.ip} (${node.vpn})` : node.ip}
              </text>}
              {/* Info */}
              {node.info && <text x={node.x - w / 2 + 10} y={node.y + (node.type === 'server' ? 18 : 26)} fontSize="8" fill="#3b4261" fontFamily="Inter, sans-serif">
                {node.info}
              </text>}
              {/* Server services mini-list */}
              {node.services && node.type === 'server' && (
                <text x={node.x - w / 2 + 10} y={node.y + 34} fontSize="8" fill={accent} fontFamily="JetBrains Mono, monospace">
                  {node.services.join(' · ')}
                </text>
              )}
            </g>
          );
        })}

        {/* Legend */}
        <g transform="translate(20, 510)">
          <line x1="0" y1="0" x2="20" y2="0" stroke="#9ece6a" strokeWidth="1.5" strokeDasharray="8,4" />
          <text x="25" y="4" fontSize="8" fill="#565f89" fontFamily="Inter, sans-serif">VPN Tunnel</text>
          <line x1="100" y1="0" x2="120" y2="0" stroke="#7aa2f7" strokeWidth="1.5" />
          <text x="125" y="4" fontSize="8" fill="#565f89" fontFamily="Inter, sans-serif">HTTPS</text>
          <line x1="180" y1="0" x2="200" y2="0" stroke="#e0af68" strokeWidth="1.5" strokeDasharray="3,3" />
          <text x="205" y="4" fontSize="8" fill="#565f89" fontFamily="Inter, sans-serif">SSH</text>
          <circle cx="270" cy="0" r="4" fill="#9ece6a" />
          <text x="278" y="4" fontSize="8" fill="#565f89" fontFamily="Inter, sans-serif">Online</text>
          <circle cx="330" cy="0" r="4" fill="#565f89" />
          <text x="338" y="4" fontSize="8" fill="#565f89" fontFamily="Inter, sans-serif">Offline</text>
        </g>
      </svg>
    </div>
  );
}


export default function GraphView({ domain = 'ops', accentColor }) {
  const accent = accentColor || `var(--accent-${domain})`;
  const [tab, setTab] = useState('topology');
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api(`/graph?domain=${domain}`).then(d => { setGraphData(d); setLoading(false); });
  }, [domain]);

  const services = graphData?.nodes || [];
  const edges = graphData?.edges || [];
  const flows = graphData?.flows || [];
  const timeline = graphData?.timeline || [];

  const selectedService = services.find(s => s.id === selected);
  const impactList = useMemo(() => {
    if (!selected) return [];
    const affected = new Set();
    const queue = [selected];
    while (queue.length > 0) {
      const id = queue.shift();
      services.forEach(s => {
        if (s.dependsOn?.includes(id) && !affected.has(s.id)) {
          affected.add(s.id);
          queue.push(s.id);
        }
      });
    }
    return services.filter(s => affected.has(s.id));
  }, [selected, services]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-md)' }}>
      {/* Sub-Tab Bar */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '8px 16px', fontSize: 'var(--font-size-sm)', background: 'none', border: 'none', cursor: 'pointer',
            color: tab === t.key ? accent : 'var(--text-muted)',
            borderBottom: tab === t.key ? `2px solid ${accent}` : '2px solid transparent',
          }}>{t.label}</button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', alignSelf: 'center' }}>
          {services.length} Services, {edges.length} Verbindungen
        </span>
      </div>

      {loading && <div style={{ color: 'var(--text-muted)' }}>Lade Graph...</div>}

      {/* TOPOLOGY */}
      {!loading && tab === 'topology' && <TopologyView accent={accent} domain={domain} />}

      {/* MAP */}
      {!loading && tab === 'map' && (
        <div style={{ display: 'flex', gap: 'var(--gap-md)' }}>
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 'var(--gap-sm)', alignContent: 'start' }}>
            {services.map(s => <ServiceBox key={s.id} service={s} accent={accent} selected={selected} onClick={s => setSelected(s.id === selected ? null : s.id)} />)}
          </div>
          {selectedService && (
            <div className="eco-detail-panel" style={{ width: 260 }}>
              <div className="card" style={{ padding: 'var(--gap-sm)', borderLeft: `3px solid ${accent}` }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{selectedService.name}</div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>Typ: {selectedService.type}</div>
                {selectedService.port && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>Port: {selectedService.port}</div>}
                {selectedService.dependsOn?.length > 0 && (
                  <div style={{ marginTop: 'var(--gap-xs)' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Abhaengig von:</div>
                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 2 }}>
                      {selectedService.dependsOn.map((d, i) => <span key={i} className="badge badge-off" style={{ fontSize: 8 }}>{d}</span>)}
                    </div>
                  </div>
                )}
                {selectedService.usedBy?.length > 0 && (
                  <div style={{ marginTop: 'var(--gap-xs)' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Genutzt von:</div>
                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 2 }}>
                      {selectedService.usedBy.map((u, i) => <span key={i} className="badge badge-off" style={{ fontSize: 8 }}>{u}</span>)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* FLOWS */}
      {!loading && tab === 'flows' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {flows.length > 0 ? flows.map((f, i) => <FlowArrow key={i} from={f.from} to={f.to} label={f.label} accent={accent} />) : (
            <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>Keine Datenfluesse fuer diese Domain</div>
          )}
        </div>
      )}

      {/* IMPACT */}
      {!loading && tab === 'impact' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-sm)' }}>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>Service auswaehlen um Impact zu sehen:</div>
          <div style={{ display: 'flex', gap: 'var(--gap-xs)', flexWrap: 'wrap' }}>
            {services.map(s => (
              <button key={s.id} className={`btn${selected === s.id ? ' btn-primary' : ''}`} onClick={() => setSelected(s.id === selected ? null : s.id)}
                style={{ fontSize: 'var(--font-size-xs)', padding: '4px 10px' }}>{s.name}</button>
            ))}
          </div>
          {selected && impactList.length > 0 && (
            <div>
              <h4 style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-warn, #e0af68)', marginBottom: 'var(--gap-xs)' }}>
                Ausfall von "{selectedService?.name}" betrifft {impactList.length} Service{impactList.length > 1 ? 's' : ''}:
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 'var(--gap-xs)' }}>
                {impactList.map(s => <ServiceBox key={s.id} service={s} accent="var(--color-warn, #e0af68)" selected={null} onClick={() => {}} />)}
              </div>
            </div>
          )}
          {selected && impactList.length === 0 && <div style={{ color: 'var(--text-muted)' }}>Keine abhaengigen Services betroffen</div>}
        </div>
      )}

      {/* TIMELINE */}
      {!loading && tab === 'timeline' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {timeline.length > 0 ? timeline.map((e, i) => (
            <div key={i} className="card" style={{ padding: 'var(--gap-xs) var(--gap-sm)', display: 'flex', alignItems: 'center', gap: 'var(--gap-sm)' }}>
              <span style={{ fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', minWidth: 80 }}>{e.date}</span>
              <span style={{ fontSize: 'var(--font-size-sm)' }}>{e.message}</span>
              {e.author && <span className="badge badge-off" style={{ fontSize: 8 }}>{e.author}</span>}
            </div>
          )) : (
            <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>Timeline wird aus Git-History und Deployment-Logs generiert</div>
          )}
        </div>
      )}
    </div>
  );
}
